import { addParamToUrl, isIOS, isInstagramInAppBrowser } from './Utils';

export const CHAIN_RESTAURANT_SHARE_PARAM = 'posId';

const EARTH_RADIUS_KM = 6371;

export function haversineDistanceKm(lat1, lng1, lat2, lng2) {
    const toRad = (deg) => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function geocodeWithGeocoder(geocoder, request) {
    return new Promise((resolve) => {
        geocoder.geocode(request, (results, status) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
                const location = results[0].geometry.location;
                resolve({ lat: location.lat(), lng: location.lng() });
                return;
            }
            resolve(null);
        });
    });
}

export async function geocodeCityOrPostalCode(query) {
    const trimmed = query?.trim();
    if (!trimmed || !window.google?.maps?.importLibrary) {
        return null;
    }

    const { Geocoder } = await window.google.maps.importLibrary('geocoding');
    const geocoder = new Geocoder();
    const baseRequest = { region: 'fr' };

    if (/^\d{5}$/.test(trimmed)) {
        const byPostalCode = await geocodeWithGeocoder(geocoder, {
            ...baseRequest,
            componentRestrictions: { country: 'FR', postalCode: trimmed },
        });
        if (byPostalCode) {
            return byPostalCode;
        }
    }

    const byAddress = await geocodeWithGeocoder(geocoder, {
        ...baseRequest,
        address: trimmed,
        componentRestrictions: { country: 'FR' },
    });
    if (byAddress) {
        return byAddress;
    }

    if (/^\d{5}$/.test(trimmed)) {
        return geocodeWithGeocoder(geocoder, {
            ...baseRequest,
            address: `${trimmed}, France`,
        });
    }

    return null;
}

export function getUserGeolocation(options = {}) {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(Object.assign(new Error('unsupported'), { code: 'unsupported' }));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            },
            (error) => reject(error),
            {
                enableHighAccuracy: false,
                timeout: 15000,
                maximumAge: 60000,
                ...options,
            },
        );
    });
}

export function hasChainRestaurants(story) {
    return Array.isArray(story?.chainRestaurants) && story.chainRestaurants.length > 0;
}

function hasNonEmptyOrderingPlatforms(platforms) {
    return Array.isArray(platforms) && platforms.some((link) => typeof link === 'string' && link.trim() !== '');
}

/** True if the story (or any chain restaurant) exposes an ordering link. */
export function storyHasOrderingPlatforms(story) {
    if (hasNonEmptyOrderingPlatforms(story?.orderingPlatforms)) {
        return true;
    }
    if (!hasChainRestaurants(story)) {
        return false;
    }
    return story.chainRestaurants.some((restaurant) => hasNonEmptyOrderingPlatforms(restaurant?.orderingPlatforms));
}

export function getChainRestaurantPosArea(restaurant) {
    return restaurant?.posArea ?? restaurant?.formattedAddress ?? null;
}

export function getRestaurantInfosDisplay(story) {
    if (!story) {
        return { posName: null, address: null };
    }

    if (hasChainRestaurants(story) && !story.posId) {
        return { posName: story.posName ?? null, address: story.posArea ?? story.formattedAddress ?? null };
    }

    return {
        posName: story.posName ?? null,
        address: story.posArea ?? story.formattedAddress ?? null,
    };
}

export function findChainRestaurantByPosId(chainRestaurants, posId) {
    if (!Array.isArray(chainRestaurants) || posId == null || posId === '') {
        return null;
    }
    return chainRestaurants.find((entry) => entry.posId === posId) ?? null;
}

export function getSharedPosIdFromUrl(search = window.location.search) {
    return new URLSearchParams(search).get(CHAIN_RESTAURANT_SHARE_PARAM);
}

export function buildStoryShareLink(story) {
    const baseLink = story?.storyLink;
    if (!baseLink) {
        return baseLink;
    }
    if (hasChainRestaurants(story) && story.posId) {
        return addParamToUrl(baseLink, CHAIN_RESTAURANT_SHARE_PARAM, story.posId);
    }
    return baseLink;
}

export function getValidChainRestaurants(chainRestaurants) {
    if (!Array.isArray(chainRestaurants)) {
        return [];
    }
    return chainRestaurants.filter((restaurant) => restaurant.lat != null && restaurant.lng != null);
}

export function getChainRestaurantsCenter(restaurants) {
    if (!restaurants.length) {
        return { lat: 46.6, lng: 2.4 };
    }
    let latSum = 0;
    let lngSum = 0;
    for (const restaurant of restaurants) {
        latSum += restaurant.lat;
        lngSum += restaurant.lng;
    }
    return { lat: latSum / restaurants.length, lng: lngSum / restaurants.length };
}

export function fitMapToChainRestaurants(map, restaurants, fallbackCenter = null) {
    if (!map || !window.google?.maps) {
        return;
    }
    if (!restaurants.length) {
        if (fallbackCenter) {
            map.setCenter(fallbackCenter);
            map.setZoom(12);
        }
        return;
    }
    if (restaurants.length === 1) {
        map.setCenter({ lat: restaurants[0].lat, lng: restaurants[0].lng });
        map.setZoom(14);
        return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    for (const restaurant of restaurants) {
        bounds.extend({ lat: restaurant.lat, lng: restaurant.lng });
    }
    map.fitBounds(bounds, 24);
    window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom() > 15) {
            map.setZoom(15);
        }
    });
}

export function findNearbyChainRestaurants(chainRestaurants, userLat, userLng, maxDistanceKm = 20) {
    if (!Array.isArray(chainRestaurants)) {
        return [];
    }

    return chainRestaurants
        .filter((restaurant) => restaurant.posId != null && restaurant.lat != null && restaurant.lng != null)
        .map((restaurant) => ({
            ...restaurant,
            distanceKm: haversineDistanceKm(userLat, userLng, restaurant.lat, restaurant.lng),
        }))
        .filter((restaurant) => restaurant.distanceKm <= maxDistanceKm)
        .sort((a, b) => a.distanceKm - b.distanceKm);
}

const CHAIN_RESTAURANT_STORY_FIELDS = [
    'city',
    'posPhone',
    'posBookingByPhone',
    'posBookingLink',
    'posChatLink',
    'orderingPlatforms',
    'noGMAPS',
];

export function resolveChainRestaurantEntry(story, selectedRestaurant) {
    if (!selectedRestaurant?.posId) {
        return null;
    }
    if (!Array.isArray(story?.chainRestaurants)) {
        return selectedRestaurant;
    }

    const match = story.chainRestaurants.find((entry) => {
        if (entry.posId !== selectedRestaurant.posId) {
            return false;
        }
        if (selectedRestaurant.posName != null && entry.posName !== selectedRestaurant.posName) {
            return false;
        }
        if (selectedRestaurant.lat != null && entry.lat !== selectedRestaurant.lat) {
            return false;
        }
        return true;
    });

    if (!match) {
        return selectedRestaurant;
    }

    return {
        ...match,
        ...(selectedRestaurant.distanceKm != null ? { distanceKm: selectedRestaurant.distanceKm } : {}),
    };
}

export function resolveChainMenuId(restaurant, chainDefaultMenuId) {
    const restaurantMenuId = restaurant?.menuId;
    if (restaurantMenuId != null && restaurantMenuId !== '') {
        return restaurantMenuId;
    }
    if (chainDefaultMenuId != null && chainDefaultMenuId !== '') {
        return chainDefaultMenuId;
    }
    return null;
}

export function applySelectedChainRestaurantToStory(story, selectedRestaurant, chainDefaultMenuId = undefined) {
    if (!story || !selectedRestaurant?.posId) {
        return story;
    }

    const restaurant = resolveChainRestaurantEntry(story, selectedRestaurant) ?? selectedRestaurant;
    const fallbackMenuId = chainDefaultMenuId !== undefined ? chainDefaultMenuId : story.menuId;

    const updatedStory = {
        ...story,
        posId: restaurant.posId,
        posName: restaurant.posName ?? story.posName,
    };

    const restaurantPosArea = getChainRestaurantPosArea(restaurant);

    if (restaurantPosArea) {
        updatedStory.posArea = restaurantPosArea;
    } else {
        delete updatedStory.posArea;
    }
    delete updatedStory.formattedAddress;

    if (restaurantPosArea && restaurant.posName) {
        updatedStory.mapsSearchQuery = restaurant.posName + ', ' + restaurantPosArea;
    } else {
        delete updatedStory.mapsSearchQuery;
    }

    for (const field of CHAIN_RESTAURANT_STORY_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(restaurant, field)) {
            updatedStory[field] = restaurant[field];
        } else {
            delete updatedStory[field];
        }
    }

    // priceLevel : entrée chaîne si définie, sinon conserver celui déjà sur story
    if (restaurant.priceLevel != null) {
        updatedStory.priceLevel = restaurant.priceLevel;
    }

    const resolvedMenuId = resolveChainMenuId(restaurant, fallbackMenuId);
    if (resolvedMenuId != null) {
        updatedStory.menuId = resolvedMenuId;
    } else {
        delete updatedStory.menuId;
    }

    return updatedStory;
}

export function getPlaceMapsQuery(story) {
    return story?.mapsSearchQuery || '';
}

/** Geocode an address / Maps search query to { lat, lng }. */
export async function geocodeAddressQuery(query) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!trimmed || !window.google?.maps?.importLibrary) {
        return null;
    }
    const { Geocoder } = await window.google.maps.importLibrary('geocoding');
    const geocoder = new Geocoder();
    return geocodeWithGeocoder(geocoder, {
        address: trimmed,
        region: 'fr',
    });
}

/**
 * Resolve the restaurant marker for the in-app location map
 * (chain lat/lng, story coords, placeDetails.location, or null to geocode later).
 */
export function resolveRestaurantMapTarget(story, placeDetails) {
    if (hasChainRestaurants(story) && story.posId) {
        const entry = findChainRestaurantByPosId(story.chainRestaurants, story.posId);
        if (entry != null && Number.isFinite(Number(entry.lat)) && Number.isFinite(Number(entry.lng))) {
            return {
                title: entry.posName || story.posName || '',
                address: getChainRestaurantPosArea(entry) || story.posArea || story.formattedAddress || '',
                position: { lat: Number(entry.lat), lng: Number(entry.lng) },
            };
        }
    }

    if (story != null && Number.isFinite(Number(story.lat)) && Number.isFinite(Number(story.lng))) {
        return {
            title: story.posName || '',
            address: story.posArea || story.formattedAddress || '',
            position: { lat: Number(story.lat), lng: Number(story.lng) },
        };
    }

    const loc = placeDetails?.location;
    if (loc != null) {
        const lat = loc.latitude ?? loc.lat;
        const lng = loc.longitude ?? loc.lng;
        if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
            const displayName = placeDetails?.displayName;
            const titleFromPlace = typeof displayName === 'string'
                ? displayName
                : (displayName?.text || '');
            return {
                title: story?.posName || titleFromPlace || '',
                address: story?.posArea || story?.formattedAddress || placeDetails?.formattedAddress || '',
                position: { lat: Number(lat), lng: Number(lng) },
            };
        }
    }

    return null;
}

export function openGoogleMapsSearch(query) {
    if (!query) {
        return false;
    }
    const url = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(query);
    if (!(isIOS() && isInstagramInAppBrowser())) {
        window.open(url);
    } else {
        window.location.href = url;
    }
    return true;
}

export function openGoogleMapsForChainStory(story) {
    return openGoogleMapsSearch(getPlaceMapsQuery(story));
}

export function buildAnswerChatStoryContext(story) {
    if (!story) {
        return null;
    }

    const isChain = hasChainRestaurants(story);
    if (isChain && !story.posId) {
        return null;
    }

    return {
        posName: story.posName ?? null,
        city: story.city ?? null,
        country: story.country ?? null,
        posArea: story.posArea ?? story.formattedAddress ?? null,
        posId: story.posId ?? null,
        mapsSearchQuery: story.mapsSearchQuery ?? null,
        menuId: story.menuId ?? null,
        menuFile: story.menuFile ?? null,
        menuFileEn: story.menuFileEn ?? null,
        posBookingLink: story.posBookingLink ?? null,
        posBookingByPhone: story.posBookingByPhone ?? null,
        posPhone: story.posPhone ?? null,
        posChatLink: story.posChatLink ?? null,
        orderingPlatforms: story.orderingPlatforms ?? null,
        website: story.website ?? null,
    };
}
